/**
 * A surface that is not there until you reach for it (RFC 0284 S6d).
 *
 * The mechanism behind `reveal: 'hover'`. The pointer arriving at an edge shows
 * the surface; leaving it hides the surface again after a beat.
 *
 * DELAYS ON BOTH SIDES, and they are not the same number. Showing waits so that
 * crossing the edge on the way somewhere else does not flash chrome at you.
 * Hiding waits longer, because the pointer leaves the strip every time it moves
 * toward something inside it, and a surface that vanishes as you reach for it
 * is worse than one that is always there.
 *
 * `WorldShell` has an older copy of this for the sidebar, written before the
 * header needed one. Left where it is: it is entangled with the sidebar's
 * device-local collapse flag, and untangling working chrome is not what S6d is
 * for.
 */

import { useEffect, useRef, useState } from 'react'

/** How close to the edge counts as reaching for it. */
const EDGE_PX = 6
const REVEAL_DELAY = 120
const HIDE_DELAY = 320

/**
 * `bottom` joined for the composer slot (S8c), and it is the one edge where the
 * measurement is not against zero: the window's height moves when the window
 * is resized, so "near the bottom" has to be computed each time rather than
 * closed over.
 */
export function useEdgeReveal(
  active: boolean,
  edge: 'top' | 'left' | 'bottom',
) {
  const [revealed, setRevealed] = useState(false)
  const revealedRef = useRef(false)
  revealedRef.current = revealed
  const showT = useRef<number | undefined>(undefined)
  const hideT = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (!active) {
      // Reset on the way out, or switching to a pinned layout leaves the
      // surface stuck in whatever state the pointer left it in.
      setRevealed(false)
      return
    }
    const onMove = (e: MouseEvent) => {
      const near =
        edge === 'top'
          ? e.clientY <= EDGE_PX
          : edge === 'bottom'
            ? e.clientY >= window.innerHeight - EDGE_PX
            : e.clientX <= EDGE_PX
      if (!near || revealedRef.current) return
      window.clearTimeout(hideT.current)
      window.clearTimeout(showT.current)
      showT.current = window.setTimeout(() => setRevealed(true), REVEAL_DELAY)
    }
    window.addEventListener('mousemove', onMove)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.clearTimeout(showT.current)
      window.clearTimeout(hideT.current)
    }
  }, [active, edge])

  return {
    revealed,
    /** On the surface's own `onMouseLeave`. */
    scheduleHide: () => {
      window.clearTimeout(hideT.current)
      hideT.current = window.setTimeout(() => setRevealed(false), HIDE_DELAY)
    },
    /** On its `onMouseEnter`, so moving INTO the surface does not dismiss it. */
    cancelHide: () => window.clearTimeout(hideT.current),
  }
}
